import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  startAfter,
  updateDoc,
  where,
  type DocumentData,
  type QueryDocumentSnapshot,
  type Timestamp,
} from 'firebase/firestore';
import { appId, db } from './firebase';

export type MonthlyImpactCampaignStatus =
  | 'draft'
  | 'approved'
  | 'scheduled'
  | 'sending'
  | 'sent'
  | 'failed'
  | 'cancelled';

export type MonthlyImpactRunStatus =
  | 'queued'
  | 'sending'
  | 'sent'
  | 'completed_with_errors'
  | 'failed'
  | 'paused_for_review';

export type MonthlyImpactRecipientStatus =
  | 'queued'
  | 'sent'
  | 'retrying'
  | 'failed_permanent'
  | 'failed_exhausted';

export interface MonthlyImpactCampaign {
  id: string;
  monthKey: string;
  title: string;
  subject: string;
  preheader: string;
  heroStoryHtml: string;
  heroStoryText: string;
  impactSummary: string;
  status: MonthlyImpactCampaignStatus;
  scheduledAt: Timestamp | Date | null;
  approvedAt: Timestamp | Date | null;
  approvedBy: string | null;
  createdAt: Timestamp | Date | null;
  updatedAt: Timestamp | Date | null;
  createdBy: string | null;
  updatedBy: string | null;
}

export interface MonthlyImpactCampaignRun {
  id: string;
  campaignId: string;
  monthKey: string;
  status: MonthlyImpactRunStatus;
  batchSize: number;
  queuedCount: number;
  sentCount: number;
  failedCount: number;
  retryCount: number;
  startedAt: Timestamp | Date | null;
  completedAt: Timestamp | Date | null;
  lastError: string | null;
  createdAt: Timestamp | Date | null;
  updatedAt: Timestamp | Date | null;
}

export interface MonthlyImpactRecipientStatusRow {
  id: string;
  campaignId: string;
  runId: string;
  donorId: string;
  status: MonthlyImpactRecipientStatus;
  attemptCount: number;
  lastError: string | null;
  sentAt: Timestamp | Date | null;
  updatedAt: Timestamp | Date | null;
}

export interface MonthlyImpactCampaignPayload {
  monthKey: string;
  title: string;
  subject: string;
  preheader: string;
  heroStoryHtml: string;
  heroStoryText: string;
  impactSummary: string;
  status: MonthlyImpactCampaignStatus;
  scheduledAt?: Date | null;
  approvedAt?: Date | null;
  approvedBy?: string | null;
}

const campaignsRef = () => collection(db, 'artifacts', appId, 'monthlyImpactCampaigns');
const runsRef = () => collection(db, 'artifacts', appId, 'monthlyImpactCampaignRuns');
const recipientStatusesRef = () => collection(db, 'artifacts', appId, 'monthlyImpactEmails');

function toCampaign(id: string, data: DocumentData): MonthlyImpactCampaign {
  return {
    id,
    monthKey: data.monthKey ?? '',
    title: data.title ?? '',
    subject: data.subject ?? '',
    preheader: data.preheader ?? '',
    heroStoryHtml: data.heroStoryHtml ?? '',
    heroStoryText: data.heroStoryText ?? '',
    impactSummary: data.impactSummary ?? '',
    status: (data.status as MonthlyImpactCampaignStatus) ?? 'draft',
    scheduledAt: data.scheduledAt ?? null,
    approvedAt: data.approvedAt ?? null,
    approvedBy: data.approvedBy ?? null,
    createdAt: data.createdAt ?? null,
    updatedAt: data.updatedAt ?? null,
    createdBy: data.createdBy ?? null,
    updatedBy: data.updatedBy ?? null,
  };
}

function toRun(id: string, data: DocumentData): MonthlyImpactCampaignRun {
  return {
    id,
    campaignId: data.campaignId ?? '',
    monthKey: data.monthKey ?? '',
    status: (data.status as MonthlyImpactRunStatus) ?? 'queued',
    batchSize: data.batchSize ?? 0,
    queuedCount: data.queuedCount ?? 0,
    sentCount: data.sentCount ?? 0,
    failedCount: data.failedCount ?? 0,
    retryCount: data.retryCount ?? 0,
    startedAt: data.startedAt ?? null,
    completedAt: data.completedAt ?? null,
    lastError: data.lastError ?? null,
    createdAt: data.createdAt ?? null,
    updatedAt: data.updatedAt ?? null,
  };
}

function toRecipientStatus(id: string, data: DocumentData): MonthlyImpactRecipientStatusRow {
  return {
    id,
    campaignId: data.campaignId ?? '',
    runId: data.runId ?? '',
    donorId: data.donorId ?? '',
    status: (data.status as MonthlyImpactRecipientStatus) ?? 'queued',
    attemptCount: data.attemptCount ?? 0,
    lastError: data.lastError ?? null,
    sentAt: data.sentAt ?? null,
    updatedAt: data.updatedAt ?? null,
  };
}

export async function listMonthlyImpactCampaigns(): Promise<MonthlyImpactCampaign[]> {
  const campaignsQuery = query(campaignsRef(), orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(campaignsQuery);
  return snapshot.docs.map((docSnap) => toCampaign(docSnap.id, docSnap.data()));
}

export async function getMonthlyImpactCampaign(campaignId: string): Promise<MonthlyImpactCampaign | null> {
  const ref = doc(db, 'artifacts', appId, 'monthlyImpactCampaigns', campaignId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return toCampaign(snap.id, snap.data());
}

export async function createMonthlyImpactCampaign(
  payload: MonthlyImpactCampaignPayload,
  actorUserId: string
): Promise<string> {
  const now = serverTimestamp();
  const created = await addDoc(campaignsRef(), {
    monthKey: payload.monthKey,
    title: payload.title,
    subject: payload.subject,
    preheader: payload.preheader,
    heroStoryHtml: payload.heroStoryHtml,
    heroStoryText: payload.heroStoryText,
    impactSummary: payload.impactSummary,
    status: payload.status,
    scheduledAt: payload.scheduledAt ?? null,
    approvedAt: payload.approvedAt ?? null,
    approvedBy: payload.approvedBy ?? null,
    createdAt: now,
    updatedAt: now,
    createdBy: actorUserId,
    updatedBy: actorUserId,
  });
  return created.id;
}

export async function updateMonthlyImpactCampaign(
  campaignId: string,
  payload: Partial<MonthlyImpactCampaignPayload>,
  actorUserId: string
): Promise<void> {
  const ref = doc(db, 'artifacts', appId, 'monthlyImpactCampaigns', campaignId);
  await updateDoc(ref, {
    ...payload,
    updatedAt: serverTimestamp(),
    updatedBy: actorUserId,
  });
}

export async function listCampaignRuns(campaignId: string): Promise<MonthlyImpactCampaignRun[]> {
  const runsQuery = query(
    runsRef(),
    where('campaignId', '==', campaignId),
    orderBy('startedAt', 'desc')
  );
  const snapshot = await getDocs(runsQuery);
  return snapshot.docs.map((docSnap) => toRun(docSnap.id, docSnap.data()));
}

export interface RecipientStatusPage {
  rows: MonthlyImpactRecipientStatusRow[];
  cursor: QueryDocumentSnapshot<DocumentData> | null;
}

export async function listCampaignRecipientStatuses(
  campaignId: string,
  pageSize = 25,
  cursor: QueryDocumentSnapshot<DocumentData> | null = null
): Promise<RecipientStatusPage> {
  const base = [
    where('campaignId', '==', campaignId),
    orderBy('updatedAt', 'desc'),
    limit(pageSize),
  ] as const;

  const q = cursor
    ? query(recipientStatusesRef(), ...base, startAfter(cursor))
    : query(recipientStatusesRef(), ...base);
  const snapshot = await getDocs(q);
  const rows = snapshot.docs.map((docSnap) => toRecipientStatus(docSnap.id, docSnap.data()));
  const nextCursor = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
  return { rows, cursor: nextCursor };
}
